import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { WorkflowDefinitionRow } from "@/workflows/types";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

interface Company {
  id: string;
  name: string;
}

export default function WorkflowDefinitionsAdmin({ fixedCompanyId, fixedCompanyName }: { fixedCompanyId?: string; fixedCompanyName?: string } = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isLoading } = useIsSuperadmin();
  const { isSuperadmin } = useIsSuperadmin();
  const { id: currentCompanyId, name: currentCompanyName } = useCurrentCompany();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", name: "", icon: "", color: "" });

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("companies").select("id, name").order("name");
      setCompanies((data as Company[]) || []);
    })();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId && currentCompanyId) {
      setSelectedCompanyId(currentCompanyId);
    }
  }, [currentCompanyId, selectedCompanyId]);

  const companyId = fixedCompanyId ?? selectedCompanyId ?? currentCompanyId;
  const companyName =
    fixedCompanyName ?? companies.find((c) => c.id === companyId)?.name ?? currentCompanyName ?? null;

  const { data: defs } = useQuery({
    queryKey: ["workflow_definitions_admin"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("workflow_definitions").select("*").order("sort_order");
      return (data ?? []) as WorkflowDefinitionRow[];
    },
  });

  const { data: toggles } = useQuery({
    queryKey: ["company_workflow_definitions", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("company_workflow_definitions")
        .select("workflow_definition_id, enabled")
        .eq("company_id", companyId);
      const map = new Map<string, boolean>();
      for (const r of (data ?? []) as { workflow_definition_id: string; enabled: boolean }[]) {
        map.set(r.workflow_definition_id, r.enabled);
      }
      return map;
    },
  });

  if (isLoading) return null;

  const isEnabled = (defId: string) => {
    const v = toggles?.get(defId);
    return v === undefined ? true : v;
  };

  const setEnabled = async (def: WorkflowDefinitionRow, enabled: boolean) => {
    if (!companyId) return toast.error("No company selected");
    const { error } = await (supabase as any)
      .from("company_workflow_definitions")
      .upsert(
        { company_id: companyId, workflow_definition_id: def.id, enabled },
        { onConflict: "company_id,workflow_definition_id" },
      );
    if (error) return toast.error(error.message);
    toast.success(`${def.name} ${enabled ? "enabled" : "disabled"} for ${companyName ?? "this company"}`);
    qc.invalidateQueries({ queryKey: ["company_workflow_definitions", companyId] });
    qc.invalidateQueries({ queryKey: ["workflow_definitions"] });
    qc.invalidateQueries({ queryKey: ["workflow_definition"] });
  };

  const create = async () => {
    if (!form.key || !form.name) return toast.error("Key and name required");
    const { error } = await (supabase as any).from("workflow_definitions").insert({
      key: form.key,
      name: form.name,
      icon: form.icon || null,
      color: form.color || null,
      sort_order: ((defs?.length ?? 0) + 1) * 10,
    });
    if (error) return toast.error(error.message);
    toast.success("Workflow created");
    setOpen(false);
    setForm({ key: "", name: "", icon: "", color: "" });
    qc.invalidateQueries({ queryKey: ["workflow_definitions_admin"] });
    qc.invalidateQueries({ queryKey: ["workflow_definitions"] });
  };

  return (
    <div className={fixedCompanyId ? "pt-4" : "min-h-screen bg-background pb-24"}>
      <div className={fixedCompanyId ? "pb-3" : "px-6 pt-6 pb-3"}>
        {!fixedCompanyId && <SettingsBackLink to="/platform/companies" label="Companies" />}
        <div>
          <h1 className="text-2xl font-bold">Workflow Definitions</h1>
          <p className="text-sm text-muted-foreground">
            Top-level workflow types and their module composition. Toggles below control availability for {companyName ?? "your company"}.
          </p>
        </div>
        {!fixedCompanyId && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="mt-3"><Plus className="w-4 h-4 mr-2" /> New workflow</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New workflow</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Key</Label><Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="quick_diagnostics" /></div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Quick Diagnostics" /></div>
              <div><Label>Icon (lucide name)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Stethoscope" /></div>
              <div><Label>Color</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="amber" /></div>
            </div>
            <DialogFooter><Button onClick={create}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>

      {isSuperadmin && !fixedCompanyId && (
        <div className="px-6 mb-4">
          <SearchableSelect
            value={selectedCompanyId ?? null}
            onChange={(val) => setSelectedCompanyId(val ?? "")}
            placeholder="Select a company"
            searchPlaceholder="Search companies…"
            emptyMessage="No companies found"
            items={companies.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      )}

      <div className={fixedCompanyId ? "space-y-3" : "px-6 space-y-3"}>
        {(defs ?? []).map((def) => {
          const enabled = isEnabled(def.id);
          return (
            <Card key={def.id} className="p-4 flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-full sm:w-auto sm:flex-1 min-w-0">
                <div className="font-medium">{def.name}</div>
                <div className="text-xs text-muted-foreground">
                  key: <code className="break-all">{def.key}</code> • global: {def.is_active ? "active" : "inactive"}
                </div>
              </div>
              <div className="flex-1 sm:flex-none flex items-center gap-2">
                <Label htmlFor={`enabled-${def.id}`} className="text-sm text-muted-foreground sm:whitespace-nowrap">
                  Enabled for company
                </Label>
                <Switch
                  id={`enabled-${def.id}`}
                  checked={enabled}
                  onCheckedChange={(v) => setEnabled(def, v)}
                  disabled={!companyId}
                />
              </div>
              <Button variant="outline" size="sm" className="shrink-0" onClick={() => navigate(`/admin/workflows/${def.key}/designer`)}>
                <Pencil className="w-4 h-4 mr-2" /> Designer
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
